// atoms/DaySelectionModal.js

import React, { useContext, useMemo, useState } from 'react';
import { CourseDesignContext } from '../context/CourseDesignContext';

const DaySelectionModal = ({ isOpen, onClose, onSelectDay, selectedQuestions = [] }) => {
    const { courseDesignState } = useContext(CourseDesignContext);
    const [selectedDayID, setSelectedDayID] = useState(null); // Track selected day

    // Helper function to get section name for a given topic dataIndex
    const getSectionName = useMemo(() => {
        const sections = courseDesignState
            .filter(item => item.dataType === 'sectionName')
            .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex));

        return (topicDataIndex) => {
            // Find the section that this topic belongs to
            const topicIndex = parseInt(topicDataIndex);
            let currentSection = null;

            for (const section of sections) {
                const sectionIndex = parseInt(section.dataIndex);
                if (sectionIndex < topicIndex) {
                    currentSection = section.dataText;
                } else {
                    break;
                }
            }

            return currentSection || 'No Section';
        };
    }, [courseDesignState]);

    // Get available days (topics with questionOptions 'yes') ordered by dataIndex
    const availableDays = useMemo(() => {
        return courseDesignState
            .filter(item => item.dataType === 'topic' && item.questionOptions === 'yes')
            .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex))
            .map(topic => ({
                dataID: topic.dataID,
                dataIndex: topic.dataIndex,
                name: topic.dataText || `Day ${topic.dataIndex}`,
                sectionName: getSectionName(topic.dataIndex)
            }));
    }, [courseDesignState, getSectionName]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div className="modal-content" style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: 'var(--standard-box-shadow)' // Use standard box shadow
            }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '20px'
                }}>
                    <p style={{ margin: 0, color: '#6c757d', fontSize: '16px' }}>
                        Select which day these questions should be imported into:
                    </p>
                    <button 
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#6c757d',
                            padding: '0',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                        title="Close"
                    >
                        ×
                    </button>
                </div>

                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                    {availableDays.length === 0 ? (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '40px 20px',
                            color: '#6c757d',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '2px dashed #dee2e6'
                        }}>
                            <div style={{ fontSize: '24px', marginBottom: '12px' }}>📅</div>
                            <p>No available days found.</p>
                            <p style={{ fontSize: '14px', margin: '8px 0 0 0' }}>
                                Make sure you have topics with questionOptions set to 'yes' in your course design.
                            </p>
                        </div>
                    ) : (
                        availableDays.map((day) => {
                            const isSelected = selectedDayID === day.dataID;
                            return (
                                <div
                                    key={day.dataID}
                                    onClick={() => setSelectedDayID(day.dataID)}
                                    style={{
                                        padding: '12px 16px',
                                        border: isSelected ? '2px solid var(--color1)' : '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        marginBottom: '8px',
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? 'var(--color1-light)' : '#fff',
                                        boxShadow: 'var(--standard-box-shadow)',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) {
                                            e.target.style.backgroundColor = '#f8f9fa';
                                            e.target.style.borderColor = 'var(--color1)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSelected) {
                                            e.target.style.backgroundColor = '#fff';
                                            e.target.style.borderColor = '#dee2e6';
                                        }
                                    }}
                                >
                                    <div style={{ 
                                        fontWeight: '600', 
                                        color: 'var(--color2)',
                                        fontSize: '16px'
                                    }}>
                                        {day.name}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                <div style={{ 
                    marginTop: '20px',
                    paddingTop: '16px',
                    borderTop: '1px solid #e9ecef',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px'
                }}>
                    <button 
                        onClick={onClose}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={() => {
                            if (selectedDayID) {
                                onSelectDay(selectedDayID);
                            }
                        }}
                        disabled={!selectedDayID}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: selectedDayID ? 'var(--color1)' : '#dee2e6',
                            color: selectedDayID ? 'white' : '#6c757d',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: selectedDayID ? 'pointer' : 'not-allowed',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Import Now
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DaySelectionModal;
